import type { User } from '@/@types/user'
import { LIST_MENTIONS, MENTIONS_SEARCH_USER } from '@/constants/api'
import { post } from '@/utils/request'
import { Loader } from '@mantine/core'
import clsx from 'clsx'
import {
	type BeautifulMentionsItem,
	type BeautifulMentionsMenuItemProps,
	type BeautifulMentionsMenuProps,
	BeautifulMentionsPlugin,
	type BeautifulMentionsSearchProps,
} from 'lexical-beautiful-mentions'
import { debounce, uniqBy } from 'lodash-es'
import { AnimatePresence, motion } from 'motion/react'
import { type HTMLAttributes, type Ref, memo } from 'react'
import { LexicalMentionNodeData } from './LexicalMentionNode'
import styles from './LexicalMentionUserPlugin.module.css'

const search = debounce(
	async (trigger: string, keywords: string | undefined | null) => {
		let users = [] as User[]
		if (keywords === '') {
			users = await post(LIST_MENTIONS).then(
				(res) =>
					uniqBy(
						res.data.data.filter((item: { type: string }) => item.type === 'USER'),
						'id'
					) as unknown as User[]
			)
		} else {
			users = await post(MENTIONS_SEARCH_USER, {
				keywords,
			}).then((res) => res.data.data as User[])
		}

		const items = users.map((user) => ({
			avatar: user.avatarImage.thumbnailUrl ?? user.avatarImage.picUrl,
			screenName: user.screenName,
			id: user.id,
			value: user.screenName,
		})) as unknown as BeautifulMentionsItem[]

		return items
	},
	600,
	{
		leading: true,
		trailing: false,
	}
) as BeautifulMentionsSearchProps['onSearch']

export interface LexicalMentionUserPluginProps extends HTMLAttributes<HTMLDivElement> {}

export default memo(function LexicalMentionUserPlugin({ ...props }: LexicalMentionUserPluginProps) {
	return (
		<BeautifulMentionsPlugin
			insertOnBlur={false}
			triggers={['@']}
			menuComponent={MentionUserMenu}
			menuItemComponent={MentionUserMenuItem}
			onSearch={async (trigger, keywords) => (await search(trigger, keywords)) ?? []}
			menuAnchorClassName={styles.mentionUserMenu}
		/>
	)
})

function MentionUserMenu({ loading, children, className, ...props }: BeautifulMentionsMenuProps) {
	return (
		<motion.ul
			initial={{
				opacity: 0,
			}}
			animate={{
				opacity: 1,
			}}
			exit={{
				opacity: 0,
			}}
			className={clsx(styles.menu, className)}
			{...props}
		>
			<AnimatePresence mode="popLayout">
				{loading ? (
					<li className={styles.loader}>
						<Loader size="xs" />
					</li>
				) : (
					children
				)}
			</AnimatePresence>
		</motion.ul>
	)
}

function MentionUserMenuItem({
	selected,
	item,
	ref,
	className,
	...props
}: BeautifulMentionsMenuItemProps & { ref?: Ref<HTMLLIElement> }) {
	const data = item.data as LexicalMentionNodeData
	if (!data) {
		return null
	}
	return (
		<li
			className={clsx(styles.menuItem, selected && styles.menuItemSelected, className)}
			ref={ref}
			{...props}
		>
			<img width={18} height={18} src={data.avatar} className={styles.avatar} />
			<span className={styles.screenName}>{data.screenName}</span>
		</li>
	)
}
